import React, { useState } from "react";
import { motion } from "framer-motion";
import SEO from '../SEO/SEO';
import { EnvelopeIcon, PhoneIcon, MapPinIcon, XMarkIcon, CheckCircleIcon, ChevronRightIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-hot-toast';
import { validateMobile, validateEmail } from '../../utils/helpers';
import { Link, useNavigate } from 'react-router-dom';

const ContactUs = () => {
  // Helper to get API URL
  const getApiUrl = (endpoint) => {
    // Use environment variable if available, otherwise check for localhost
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocal ? 'http://localhost:8001' : '';
    return `${baseUrl}${endpoint}`;
  };

  const [loanType, setLoanType] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("Anakapally");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // OTP States
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // Check if all required fields are filled
  const isFormValid = loanType && loanAmount && fullName && email && phone && validateEmail(email) && validateMobile(phone);

  // Check if submit button should be enabled
  const isSubmitEnabled = isFormValid && otpVerified;

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!phone) {
      toast.error("Please enter your phone number first");
      return;
    }
    if (!validateMobile(phone)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsSendingOtp(true);

    try {
      const response = await fetch(getApiUrl('/api/otp/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        toast.success("OTP sent to your mobile number!");
        // If in dev mode, log it locally for convenience
        if (data.dev_otp) {
          console.log('Dev OTP:', data.dev_otp);
        }
      } else {
        toast.error(data.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP Send error:", error);
      toast.error("An error occurred while sending OTP. Please try again later.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    const verifyToast = toast.loading("Verifying OTP...");

    try {
      const response = await fetch(getApiUrl('/api/otp/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpVerified(true);
        toast.success("OTP verified successfully!", { id: verifyToast });
      } else {
        toast.error(data.message || "Invalid or expired OTP", { id: verifyToast });
      }
    } catch (error) {
      console.error("OTP Verify error:", error);
      toast.error("An error occurred during verification.", { id: verifyToast });
    }
  };

  // Reset OTP state when phone number changes
  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpVerified) {
      toast.error("Please verify your phone number with OTP first");
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Sending your inquiry...");

    try {
      const response = await fetch(getApiUrl('/api/contact/submit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          loanType,
          loanAmount,
          fullName,
          email,
          phone,
          message
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || "Your inquiry has been sent!", { id: loadingToast });
        navigate('/thank-you');
        // Reset form fields
        setLoanType("");
        setLoanAmount("");
        setFullName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setOtpSent(false);
        setOtp("");
        setOtpVerified(false);
      } else {
        toast.error(data.message || "Failed to send inquiry. Please try again.", { id: loadingToast });
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("An error occurred while sending your inquiry. Please try again later.", { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const branches = [
    {
      name: "Anakapally",
      region: "AP",
      inception: "25-Mar-25",
      address: "10-06-68/1, II Floor, Pillavaari Veedhi, Anakapalli - 531001",
      contact: "9176032225",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=83.0000%2C17.6800%2C83.0100%2C17.6900&amp;layer=mapnik&amp;marker=17.6850%2C83.0050",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=17.6850&mlon=83.0050#map=15/17.6850/83.0050",
    },
    {
      name: "Guntur",
      region: "AP",
      inception: "11-Feb-22",
      address: "6-4-9/1, Arundelpet, Guntur - 522001",
      contact: "7824852579",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=80.4300%2C16.3000%2C80.4400%2C16.3100&amp;layer=mapnik&amp;marker=16.3050%2C80.4350",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=16.3050&mlon=80.4350#map=15/16.3050/80.4350",
    },
    {
      name: "Nellore",
      region: "AP",
      inception: "20-Mar-23",
      address: "3rd floor, Sridhar's Krishna Towers, Annamayya circle, Mini By Pass Road, Nellore - 524003",
      contact: "7824045935",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=79.9700%2C14.4300%2C79.9800%2C14.4400&amp;layer=mapnik&amp;marker=14.4350%2C79.9750",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=14.4350&mlon=79.9750#map=15/14.4350/79.9750",
    },
    {
      name: "Rajamundry",
      region: "AP",
      inception: "26-Feb-25",
      address: "DNO:79-02-04/1B, Tilak Road Revenue Ward No:30, Rajahmundry, Andhra Pradesh - 533103",
      contact: "9989796410",
      mapUrl: "https://www.openstreetmap.org/export/embed.html?bbox=81.7600%2C16.9900%2C81.7700%2C17.0000&amp;layer=mapnik&amp;marker=16.9950%2C81.7650",
      largerMapUrl: "https://www.openstreetmap.org/?mlat=16.9950&mlon=81.7650#map=15/16.9950/81.7650",
    },
  ];

  const selectedBranchData = branches.find((branch) => branch.name === selectedBranch);

  // Schema for SEO
  const contactSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED is an affordable housing finance company helping customers access home loans with simple and supportive processes.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600034",
          "addressCountry": "IN"
        },
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ],
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "contactType": "customer support",
            "telephone": "+91 78239 11555",
            "email": "info@ahamhfc.com",
            "areaServed": "IN",
            "availableLanguage": [
              "English",
              "Tamil"
            ]
          }
        ]
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across customer segments in India.",
        "serviceType": [
          "Home Loans",
          "Affordable Housing Finance"
        ],
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600034",
          "addressCountry": "IN"
        },
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/contact-aham-housing-finance/#webpage",
        "url": "https://ahamhfc.com/contact-aham-housing-finance",
        "name": "Contact Aham Housing Finance",
        "description": "Get in touch with AHAM HOUSING FINANCE LIMITED for housing finance support, customer assistance, and branch-related enquiries.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "contentUrl": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png"
        }
      },
      {
        "@type": "ContactPage",
        "@id": "https://ahamhfc.com/contact-aham-housing-finance/#contactpage",
        "url": "https://ahamhfc.com/contact-aham-housing-finance",
        "name": "Contact Aham Housing Finance",
        "mainEntity": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "about": {
          "@id": "https://ahamhfc.com/#financialservice"
        },
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/contact-aham-housing-finance/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contact",
            "item": "https://ahamhfc.com/contact-aham-housing-finance"
          }
        ]
      }
    ]
  };
  
   // Schema for SEO

  return (
    <div className="bg-gray-50 font-sans text-gray-900 overflow-hidden">

      <SEO
        title="Contact Aham Housing Finance: Expert Support & Customer Care"
        description="Contact Aham Housing Finance for affordable home loan support. Get expert assistance, customer care numbers, branch addresses, and office locations. We are here to help!"
        keywords="Aham Housing Finance contact, Aham contact, contact Aham Housing Finance, Aham customer care, Aham customer support, housing finance contact, home loan customer care, Aham office address, Aham Chennai office, Aham phone number, Aham email address, home loan support India, housing loan enquiry contact, Aham branch contact, customer service Aham Housing Finance, Aham Housing Finance help desk, contact home loan company, Aham support team, Aham Housing Finance customer care number, get in touch Aham Housing Finance"
        canonical="https://ahamhfc.com/contact-aham-housing-finance"
        ogTitle="Contact Aham Housing Finance: Expert Support & Customer Care"
        ogDescription="Contact Aham Housing Finance for affordable home loan support. Get expert assistance, customer care numbers, branch addresses, and office locations. We are here to help!"
        schema={contactSchema}
      />

      {/* Hero Section */}
      <section className="relative h-[20rem] md:h-[32rem] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-primary-700/70"></div>
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.3, ease: "easeOut" }}
          className="relative z-10 text-center px-6"
        >
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg"
          >
            Contact AHAM Housing Finance
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="text-white/90 text-lg md:text-xl max-w-lg mx-auto"
          >
            Let us help you find the perfect home financing solution today.
          </motion.p>

          {/* Breadcrumb Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center justify-center space-x-2 mt-6"
          >
            <Link
              to="/"
              className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 text-white/60" />
            <span className="text-yellow-300 text-sm font-semibold">Contact Us</span>
          </motion.div>

        </motion.div>
      </section>

      {/* Main Section */}
      <section className="max-w-7xl mx-auto px-4 -mt-12 md:-mt-40 relative z-20 mb-16">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="bg-white rounded-2xl p-5 md:p-10 shadow-xl space-y-8"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-blue-900 text-center md:text-left">Connect With Us</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Our dedicated team is ready to assist you with your housing finance needs. Get in touch now.
            </p>
            <div className="grid gap-6">
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="flex items-center space-x-4 bg-blue-50 rounded-xl p-4 md:p-5 shadow-md hover:shadow-lg transition-all"
              >
                <MapPinIcon className="h-7 w-7 text-blue-700 shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900">Address</h3>
                  <p className="text-gray-600 text-sm md:text-base">
                    New No. 2 & 4, Old No. 24 & 25,<br className="hidden md:block" />
                    3rd Floor, Sivaganga Road,<br />
                    Nungambakkam, Chennai - 600034
                  </p>
                </div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="flex items-center space-x-4 bg-blue-50 rounded-xl p-4 md:p-5 shadow-md hover:shadow-lg transition-all"
              >
                <PhoneIcon className="h-7 w-7 text-blue-700 shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900">Phone</h3>
                  <p className="text-gray-600 text-sm md:text-base">78239 11555</p>
                </div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="flex items-center space-x-4 bg-blue-50 rounded-xl p-4 md:p-5 shadow-md hover:shadow-lg transition-all"
              >
                <EnvelopeIcon className="h-7 w-7 text-blue-700 shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900">Email</h3>
                  <p className="text-gray-600 text-sm md:text-base">info@ahamhfc.com</p>
                </div>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="flex items-start space-x-4 bg-blue-50 rounded-xl p-4 md:p-5 shadow-md hover:shadow-lg transition-all"
              >
                <ShieldCheckIcon className="h-7 w-7 text-blue-700 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">Grievance Redressal</h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    If your concern remains unresolved after contacting our support team, please follow our{' '}
                    <Link
                      to="/grievance-redressal-policy-customer-support"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      <strong><u>grievance redressal process</u></strong>
                    </Link>{' '}
                    for further assistance.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="bg-white rounded-2xl p-5 md:p-10 shadow-xl space-y-6"
          >
            <h2 className="text-2xl md:text-4xl font-bold text-blue-900 text-center md:text-left">Start Your Loan Journey</h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Fill out the form below to get started with your loan application.
            </p>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="loanType" className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Type*
                </label>
                <select
                  id="loanType"
                  value={loanType}
                  onChange={(e) => setLoanType(e.target.value)}
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                >
                  <option value="">Select Loan Type</option>
                  <option value="homeConstruction">Home Construction Loan</option>
                  <option value="purchaseResale">Home Purchase or Resale Loan</option>
                  <option value="compositeLoan">Composite Loan</option>
                  <option value="homeRenovation">Home Renovation Loan</option>
                  <option value="homeExtension">Home Extension Loan</option>
                  <option value="balanceTransfer">Balance Transfer Loan</option>
                  <option value="nriHome">NRI Home Loan</option>
                  <option value="topUpLoan">Top-up Loan</option>
                  <option value="mortgage">Mortgage Loan</option>
                </select>
              </div>
              <div>
                <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Amount (INR)*
                </label>
                <input
                  type="number"
                  id="loanAmount"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  placeholder="Enter loan amount"
                  className="w-full p-3 md:p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                />
              </div>
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name*
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address*
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full p-3 md:p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                  required
                />
              </div>
              {/* Phone Number with OTP */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number*
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                    className={`flex-1 p-4 border rounded-xl focus:ring-2 focus:ring-blue-600 transition ${otpVerified ? 'border-green-500 bg-green-50' : 'border-gray-200'
                      }`}
                    required
                    disabled={otpVerified}
                  />
                    {!otpVerified && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp || !phone || phone.length < 10}
                        className={`px-3 md:px-4 py-2 rounded-xl font-medium transition-all text-sm md:text-base ${isSendingOtp || !phone || phone.length < 10
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                      >
                        {isSendingOtp ? 'Sending...' : otpSent ? 'Resend' : 'Send OTP'}
                      </button>
                    )}
                  {otpVerified && (
                    <div className="flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-xl font-medium">
                      <CheckCircleIcon className="h-5 w-5 mr-1" />
                      Verified
                    </div>
                  )}
                </div>
              </div>

              {/* OTP Input - Only show if OTP sent and not yet verified */}
              {otpSent && !otpVerified && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-blue-50 p-4 rounded-xl border border-blue-200"
                >
                  <label htmlFor="otp" className="block text-sm font-medium text-blue-800 mb-2">
                    Enter OTP sent to your mobile
                  </label>
                  <div className="flex flex-col md:flex-row gap-3">
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="w-full md:flex-1 p-4 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-600 transition text-center text-lg tracking-widest"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={otp.length < 6}
                      className={`w-full md:w-auto px-6 py-3 md:py-2 rounded-xl font-medium transition-all ${otp.length < 6
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                      Verify
                    </button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Please enter the 6-digit code sent to your mobile number.
                  </p>
                </motion.div>
              )}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Provide any additional information (e.g., purpose of loan)"
                  rows="4"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 transition"
                ></textarea>
              </div>
              <motion.button
                whileHover={isSubmitEnabled && !isSubmitting ? { scale: 1.05 } : {}}
                whileTap={isSubmitEnabled && !isSubmitting ? { scale: 0.98 } : {}}
                disabled={!isSubmitEnabled || isSubmitting}
                className={`w-full py-3 rounded-xl font-semibold shadow-md transition-all ${isSubmitEnabled && !isSubmitting
                  ? 'bg-blue-900 text-white hover:bg-blue-800 cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : !otpVerified ? 'Verify Phone to Submit' : 'Submit Application'}
              </motion.button>
              {!isSubmitEnabled && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  {!isFormValid
                    ? 'Please fill all required fields'
                    : 'Please verify your phone number with OTP to submit'}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default ContactUs;